/* eslint-disable complexity */
import { fromJS } from 'immutable';
import md5 from 'js-md5';
import moment from 'moment';
import {
    atou,
    ISO_8601_MS_RESOLUTION,
    findContactFromChatUserKey,
} from 'flow/utils';
import { getClientType } from 'flow/utils/app-utils';
import { findExtension } from 'flow/utils/extension-utils';
import {
    receiveTopMessage,
    receiveMessageRead,
    receiveChatSessionUnread,
    receiveTopComment,
    receiveTopAddAdmin,
    receiveTopRemoveAdmin,
    receiveTopRemoveMemberFromChat,
    receiveTopAddMemberToChat,
    updateReactions,
} from 'flow/actions/chat-actions';
import {
    receiveTopPresenceNotification,
    receiveTopVoiceMessage,
} from 'flow/actions/extension-actions';
import { receiveTopPbxVoiceMessage } from 'flow/actions/pbx-actions';
import {
    handleCompanionCallControl,
    handleCompanionDesignation,
    handleCompanionLocationStatus,
    reportKnownCompanionLocationStatus,
    sweepCompanionMuteState,
} from 'flow/companion/companion-actions';
import {
    receiveIsTyping,
    channelSessionBadgeCount,
    receiveChannelMessageRead,
    receiveArchived,
    receiveUnarchived,
} from 'flow/actions/channels-actions';
import {
    onReceivedTopEventPushOfTypeOmniErrand,
    onReceivedTopEventPushOfTypeAssignedOmniErrand,
} from 'flow/actions/omnichannel-actions';
import {
    onTopEventTicketWhatsAppTemplateMessage,
    onTopEventPushOfTypeTicketAssigneeSet,
    onTopEventPushOfTypeTicketStateSet,
    onTopEventPushOfTypeTicketInternalNoteReceived,
    onTopEventPushOfTypeTicketWhatsAppMessageReceived,
    onTopEventPushOfTypeTicketSmsReceived,
    onTopEventPushOfTypeTicketCreatedReceived,
    onTopEventPushOfTypeTicketMembersChanged,
    onTopEventTicketAwaitingActivitySet,
    onTopEventTicketVisibilitySet,
    onTopEventTicketMessageStatusSet,
    onTopEventTicketSubjectSet,
    onTopEventPushOfTypeTicketWhatsAppUnsupportedMessage,
    onTopEventPushTypeTicketsBadgeCount,
    onTopEventTicketTranscriptionProgress,
    onTopEventPushTypeTicketEmailMessageReceived,
} from 'flow/actions/tickets-action';
import {
    receiveSyncStart,
    receiveSyncStop,
    receiveExtension,
    receiveActiveProfile,
    receiveNewChannel,
    receiveMexIn,
    receiveMexOut,
    receiveNewState,
    receiveHangup,
    receiveGoAway,
    receiveQueueAgentActivity,
    receiveQueueJoinEvent,
    receiveQueueLeaveEvent,
    receiveNumberInfo,
    receiveLink,
    receiveQueueCallInfo,
    receiveBagVariable,
    receiveCoMonitoredStart,
    receiveCoMonitoredEnd,
    receiveQueueMembers,
    receiveAttendedTransferCancelled,
    receiveExternalChannel,
    receiveMergedIntoConference,
    receiveUpdateCallerID,
    onTopEventCallRecordingTranscriptionProgress,
} from 'flow/actions/top-actions';
import {
    updateMissedCallState,
    spawnLastAgentNotification,
    comeBackAPICallbacks,
} from 'flow/actions/app-actions';
import { addTopLogEntry } from 'flow/utils/browser-utils';
import {
    recieveMeetingMemberCount,
    clearMeetingMemberCountsAction,
} from 'flow/actions/meeting-actions';
import appConfig from 'app-config';
import isElectron from 'is-electron';
import { getOwnExtensionKey } from 'flow/selectors/selectors';
import {
    handleVideo2IncomingCall,
    handleVideo2CallDeclined,
    handleVideo2CallAccepted,
    handleVideo2CallCanceled,
    handleVideo2CallHandledOnOtherDevice,
} from 'flow/actions/video2-actions';
import {
    onTopEventCallRecordActionAdded,
    onTopEventCallRecordActionRemoved,
} from 'flow/actions/PbxActions';
import { roomTypes } from 'flow/utils/message-utils-TW';

const PING_INTERVAL_MS = 10000;
const PING_TIMEOUT_MS = 30 * 1000;

// How long a probe (on wake-up or network change) waits for anything from Top before giving the
// connection up. Short, because the caller has a reason to doubt the socket.
const PROBE_TIMEOUT_MS = 5000;

// How long a new socket may take to open. A connect that hangs on a network that went away (for
// example across a sleep) would otherwise hold off every reconnect until the browser gives up.
const CONNECT_TIMEOUT_MS = 15000;

const CLOSE_EVENT_NORMAL_CODE = 1000;

const queueMemberEventsByQueue = new Map();
let syncMessages = [];

let socket;
// The live connection's own handles, so that only the socket that is current can act on the
// module state. See createSocket.
let currentConnection = null;
let requestId = 0;
const pendingRequests = new Map();

// Exported for tests only
export const _sendAsyncRequest = (request) => {
    return new Promise((resolve, reject) => {
        request.ActionID = requestId++;
        const timeoutId = setTimeout(() => {
            pendingRequests.delete(request.ActionID);
            reject({ reason: 'TIMED_OUT', request });
        }, 5000);
        pendingRequests.set(request.ActionID, {
            resolve,
            reject,
            timeoutId,
            request,
        });
        _sendRequest(request);
    });
};

// Exported for tests only
export const _sendRequest = (request) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
        addTopLogEntry(
            'Tried to send request to top but no connection, request: ',
            request,
        );
        return;
    }
    socket.send(JSON.stringify(request));
};

const handleOk = (response) => {
    const actionId = Number(response.ActionID);

    if (isNaN(actionId) || !pendingRequests.has(actionId)) {
        return;
    }

    const pendingRequest = pendingRequests.get(actionId);
    pendingRequests.delete(actionId);
    clearTimeout(pendingRequest.timeoutId);
    pendingRequest.resolve(response);
};

const handleError = (response) => {
    const actionId = Number(response.ActionID);

    if (isNaN(actionId) || !pendingRequests.has(actionId)) {
        return;
    }

    const pendingRequest = pendingRequests.get(actionId);
    pendingRequests.delete(actionId);
    clearTimeout(pendingRequest.timeoutId);
    pendingRequest.reject({ reason: 'ERROR', response });
};

export const mergeIntoConference = (uniqueIdToKeep, uniqueIdsToMerge) => {
    const request = {
        Action: 'MergeIntoConference',
        CallToKeep: uniqueIdToKeep,
        AdditionalCalls: uniqueIdsToMerge,
    };
    return _sendAsyncRequest(request);
};

export const hangupCall = (uniqueId) => {
    const request = {
        Action: 'Hangup',
        UniqueID: uniqueId,
    };
    _sendRequest(request);
};

/**
 * Whether there is a usable connection to TOP right now.
 *
 * Exposed so a caller with a second way to reach its destination can pick it instead of sending
 * into a closed socket - `_sendRequest` drops the frame silently, and `_sendAsyncRequest` would
 * sit on it until the 5s timeout before anyone found out.
 */
export const isConnected = () =>
    !!socket && socket.readyState === WebSocket.OPEN;

/**
 * Ask Top whether the connection is still alive, giving it up if nothing comes back shortly.
 * Sending on a dead socket does not fail - the bytes are only buffered - so after a sleep or a
 * network change the only way to find out is to wait for an answer.
 */
export const probeTopConnection = () => {
    if (currentConnection) {
        currentConnection.probe();
    }
};

/**
 * Close the Top connection, or the one still being opened, without reconnecting.
 */
export const closeTopConnection = (reason) => {
    if (currentConnection) {
        currentConnection.abandon(reason);
    }
};

/**
 * Relay a call-control command to this user's companion (the desktop app that owns the media).
 *
 * Sent to TOP rather than to Home, which used to relay it back into TOP as a push: the CTI already
 * holds this socket, so the round-trip bought nothing. TOP resolves the call's legs from the
 * channel state it already has in memory, so - unlike Home - it needs neither a database lookup
 * nor a callIds array from us.
 *
 * `deviceId` says which of the user's desktop installs the command is for, and has to travel: TOP
 * hands the command to every FLOW_DESKTOP connection on the anumber and fans out to the other
 * nodes, so nothing about delivery narrows it to one machine. The receiving side refuses anything
 * not addressed to it. Home stamps the account's designated device rather than trusting the body;
 * TOP cannot, that being the database lookup this path exists to avoid, so it carries ours through
 * (see Telavox/top#668).
 *
 * @returns {Promise} resolves on `Response: OK`, rejects with `{reason: 'ERROR'}` when TOP refuses
 *          the command and `{reason: 'TIMED_OUT'}` when no answer arrives within 5s
 */
export const companionCommand = ({
    command,
    callId,
    number,
    digits,
    nonce,
    deviceId,
}) => {
    const request = {
        Action: 'CompanionCommand',
        Command: command,
        Nonce: nonce,
    };
    if (callId) {
        request.CallID = callId;
    }
    if (number) {
        request.Number = number;
    }
    if (digits) {
        request.Digits = digits;
    }
    if (deviceId) {
        request.DeviceID = deviceId;
    }

    return _sendAsyncRequest(request);
};

/**
 * Report this client's microphone state to TOP, which records it on the named channel as the
 * `telavox.companion.locally-muted` bag variable and relays it to the user's other clients.
 *
 * Fire-and-forget: advisory display state, and the next mute corrects a lost frame.
 *
 * @param {boolean} muted
 * @param {string} [channel] this call's channel name. Optional - with one call up TOP uses it
 *     anyway; with two and no name it drops the report.
 */
export const setCompanionMute = (muted, channel) => {
    const request = {
        Action: 'SetCompanionMute',
        Muted: muted ? 'true' : 'false',
    };
    if (channel) {
        request.Channel = channel;
    }

    _sendRequest(request);
};

export const pickupQueueCall = (queue, queueId, pickerNumber) => {
    const request = {
        Action: 'PickupQueue',
        Queue: queue,
        QueueID: queueId,
        Number: pickerNumber,
        AutoAnswer: true,
    };
    _sendRequest(request);
};

export const pickupCall = (uniqueId, pickerNumber) => {
    const request = {
        Action: 'CallPickup',
        UniqueID: uniqueId,
        Extension: pickerNumber,
        Barge: false,
        AutoAnswer: true,
    };
    _sendRequest(request);
};

export const pushAgentCallback = (actions, tpn) => {
    if (!tpn.silent) {
        const { description } = tpn.data;

        const pushData = {
            description,
            sent: moment().format('HH:mm'),
        };
        return actions.push(spawnLastAgentNotification(pushData));
    }

    return undefined;
};

//Since messages will be received decoded from top we want to hide them to keep the privacy intact
const enchipherMessage = (app, message) => {
    const pushedMessage = message ? message : '';
    if (!app.get('temporaryPasswordState')) {
        return pushedMessage;
    } else {
        return 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.';
    }
};

const extensionKeyForChatUser = (getState, chatUserKey) =>
    findExtension(getState, (e) => e.get('chatUserKey') === chatUserKey).get(
        'key',
    );

/**
 * The quoted part of a reply, in the shape a message row renders it: the row
 * shows the block only when quotedMessageKey is set, then reads content,
 * sender.extensionKey and sent. The push carries all of that, just under
 * different names, so mapping it here means a reply arriving while the chat is
 * open renders as a reply immediately instead of as plain text until the
 * refetch lands.
 */
export const _quotedMessageFromPush = (getState, data) =>
    data.quotedMessageKey
        ? {
              quotedMessageKey: data.quotedMessageKey,
              content: data.quotedMessageContent,
              sent: data.quotedMessageSent,
              sender: {
                  extensionKey: extensionKeyForChatUser(
                      getState,
                      data.quotedMessageSenderKey,
                  ),
              },
          }
        : undefined;

const pushToActions = (getState, tpn) => {
    const actions = [];
    const { app, chats } = getState();

    if (tpn.type === 'chat_newmessage') {
        const me = app.getIn(['extension', 'chatUserKey']);
        const {
            sessionKey,
            senderKey,
            messageKey,
            message,
            title,
            threadMessageKey,
        } = tpn.data;

        const contact = findContactFromChatUserKey(
            getState,
            sessionKey,
            senderKey,
        );

        const quotedMessage = _quotedMessageFromPush(getState, tpn.data);

        const chatUserDTO = {
            key: senderKey,
            contact: contact,
            extensionKey: extensionKeyForChatUser(getState, senderKey),
        };

        const messageDTO = fromJS({
            key: messageKey,
            // A title means a post. Naming the type here is what lets the
            // message list pick the post card straight away, and keeps an
            // ordinary message in its sender's run instead of starting a new
            // one until the fetched copy supplies the type.
            type: title ? 'post' : 'message',
            from: chatUserDTO,
            message: enchipherMessage(app, message),
            title: title,
            read: me === senderKey,
            sent: moment().format(ISO_8601_MS_RESOLUTION),
            source: 'Top',
            ...(quotedMessage ? { quotedMessage } : {}),
            threadMessageKey,
        });

        actions.push(receiveTopMessage(sessionKey, messageDTO, tpn.silent));
    } else if (tpn.type === 'last_queue_member') {
        pushAgentCallback(actions, tpn);
    } else if (tpn.type === 'last_channel_member') {
        pushAgentCallback(actions, tpn);
    } else if (tpn.type === 'chat_newcomment') {
        const { sessionKey, senderKey, messageKey, commentKey, message } =
            tpn.data;
        const contact = findContactFromChatUserKey(
            getState,
            sessionKey,
            senderKey,
        );

        const chatUserDTO = fromJS({
            key: senderKey,
            contact: contact,
            extensionKey: findExtension(
                getState,
                (e) => e.get('chatUserKey') === senderKey,
            ).get('key'),
        });
        const comment = fromJS({
            key: commentKey,
            chatMessageKey: messageKey,
            from: chatUserDTO,
            message: enchipherMessage(app, message),
            sent: moment().format(ISO_8601_MS_RESOLUTION),
            source: 'Top',
        });

        actions.push(receiveTopComment(sessionKey, comment));
    } else if (tpn.type === 'chat_messageread') {
        const { sessionKey, messageKey, channelKey } = tpn.data;

        const session = chats.getIn(['sessions', sessionKey]);

        // Missing session in list might be a channel message therefore we don't add them.
        if (session) {
            const messageDTO = {
                key: messageKey,
                read: true,
            };
            actions.push(receiveMessageRead(sessionKey, messageDTO));
        } else if (channelKey) {
            const messageDTO = {
                key: messageKey,
                read: true,
            };

            actions.push(
                receiveChannelMessageRead(channelKey, sessionKey, messageDTO),
            );
        }
    } else if (tpn.type === 'chat_messageunread') {
        const { data } = tpn;
        const chatSessionDTO = {
            key: data.sessionKey,
            lastReadMessage: data.messageKey,
            numberOfUnreadMessages: 1,
        };
        actions.push(receiveChatSessionUnread(chatSessionDTO));
    } else if (tpn.type === 'reachable') {
        const { data } = tpn;
        const loggedinKey = getOwnExtensionKey(getState());
        const availableExtensionKey = data.key;
        const contact = findExtension(
            getState,
            (e) => e.get('key') === availableExtensionKey,
        ).get('contact');

        // Add the contact to the data received from Top. The notification needs a name and an avatar.
        const targetExtensionDTO = fromJS({
            ...data,
            contact: contact,
        });

        actions.push(
            receiveTopPresenceNotification(
                loggedinKey,
                targetExtensionDTO,
                tpn.silent,
            ),
        );
    } else if (tpn.type === 'dto') {
        const { data } = tpn;

        if (data.dtotype === 'VideoSessionDTO') {
            addTopLogEntry(
                'User was contacted by 1-1 video, this function is deprecated and will not be presented',
            );
        } else if (data.dtotype === 'IsTypingDTO') {
            const dtodata = atou(data.dto);
            const isTypingDTO = JSON.parse(dtodata);

            const channelKey = isTypingDTO.channel.key;
            const chatSessionKey = isTypingDTO.chatSession.key;
            const extensionKey = data.from;
            const typing = isTypingDTO.typing;

            actions.push(
                receiveIsTyping(
                    channelKey,
                    chatSessionKey,
                    extensionKey,
                    typing,
                ),
            );
        }
    } else if (tpn.type === 'voicemail') {
        const { data } = tpn;
        const loggedinKey = getOwnExtensionKey(getState());

        const voiceMessageDTO = fromJS({
            uniqueId: data.uniqueId,
            number: data.lastCallerId,
            voiceMessageState: 'unread',
        });

        actions.push(
            receiveTopVoiceMessage(loggedinKey, voiceMessageDTO, tpn.silent),
        );
    } else if (tpn.type === 'agent_new_message') {
        const me = app.getIn(['extension', 'chatUserKey']);
        const { data } = tpn;
        const { channelDTO, messageDTO, sessionDTO } = JSON.parse(
            atou(data.dto),
        );

        const senderKey = messageDTO.from.key;
        const contact = findContactFromChatUserKey(
            getState,
            sessionDTO.key,
            senderKey,
        );

        const chatUserDTO = {
            key: senderKey,
            contact: contact,
            extensionKey: findExtension(
                getState,
                (e) => e.get('chatUserKey') === senderKey,
            ).get('key'),
        };

        const messageObj = fromJS({
            key: messageDTO.key,
            channelKey: channelDTO.key,
            from: chatUserDTO,
            message: enchipherMessage(app, messageDTO.message),
            read: me === senderKey || contact,
            sent: moment().format(ISO_8601_MS_RESOLUTION),
            source: 'Top',
        });

        //increase badge count for non muted sessions
        if (!tpn.silent) {
            actions.push(channelSessionBadgeCount(channelDTO.key));
        }

        actions.push(receiveTopMessage(sessionDTO.key, messageObj, tpn.silent));
    } else if (tpn.type === 'voicemail_shared') {
        const { data } = tpn;

        const voiceMessageDTO = fromJS({
            uniqueId: data.uniqueId,
            number: data.lastCallerId,
            voiceMessageState: 'unread',
        });

        actions.push(
            receiveTopPbxVoiceMessage(
                data.voicemailEntityKey,
                voiceMessageDTO,
                tpn.silent,
            ),
        );
    } else if (tpn.type === 'call_missed') {
        //increase badge count for non silent pushes
        if (!tpn.silent) {
            actions.push(updateMissedCallState());
        }
    } else if (tpn.type === 'call_record_action_added') {
        actions.push(onTopEventCallRecordActionAdded(tpn));
    } else if (tpn.type === 'call_record_action_removed') {
        actions.push(onTopEventCallRecordActionRemoved(tpn));
    } else if (tpn.type === 'chat_added_as_admin') {
        const { sessionKey, chatuserKey } = tpn.data;
        actions.push(receiveTopAddAdmin(sessionKey, chatuserKey));
    } else if (tpn.type === 'chat_remuser') {
        const { sessionKey, chatuserKey } = tpn.data;
        actions.push(receiveTopRemoveMemberFromChat(sessionKey, chatuserKey));
    } else if (tpn.type === 'chat_adduser') {
        const { sessionKey, chatuserKey, extensionKey, roomType } = tpn.data;
        if (roomType !== roomTypes.external) {
            actions.push(
                receiveTopAddMemberToChat(
                    sessionKey,
                    chatuserKey,
                    extensionKey,
                ),
            );
        }
    } else if (tpn.type === 'chat_removed_as_admin') {
        const { sessionKey, chatuserKey } = tpn.data;
        actions.push(receiveTopRemoveAdmin(sessionKey, chatuserKey));
    } else if (tpn.type === 'omni_errand') {
        actions.push(onReceivedTopEventPushOfTypeOmniErrand(tpn));
    } else if (tpn.type === 'assigned_omni_errand') {
        actions.push(onReceivedTopEventPushOfTypeAssignedOmniErrand(tpn));
    } else if (tpn.type === 'tickets_badge_count') {
        actions.push(onTopEventPushTypeTicketsBadgeCount(tpn));
    } else if (tpn.type === 'ticket_assignee_set') {
        actions.push(onTopEventPushOfTypeTicketAssigneeSet(tpn));
    } else if (tpn.type === 'ticket_status_set') {
        actions.push(onTopEventPushOfTypeTicketStateSet(tpn));
    } else if (tpn.type === 'ticket_new_internal_note') {
        actions.push(onTopEventPushOfTypeTicketInternalNoteReceived(tpn));
    } else if (tpn.type === 'ticket_new_sms_note') {
        actions.push(onTopEventPushOfTypeTicketSmsReceived(tpn));
    } else if (tpn.type === 'ticket_new_whatsapp_message') {
        actions.push(onTopEventPushOfTypeTicketWhatsAppMessageReceived(tpn));
    } else if (tpn.type === 'ticket_new_email_message') {
        actions.push(onTopEventPushTypeTicketEmailMessageReceived(tpn));
    } else if (tpn.type === 'ticket_new_whatsapp_template_message') {
        actions.push(onTopEventTicketWhatsAppTemplateMessage(tpn));
    } else if (tpn.type === 'ticket_message_status_set') {
        actions.push(onTopEventTicketMessageStatusSet(tpn));
    } else if (tpn.type === 'ticket_unsupported_whatsapp_message') {
        actions.push(onTopEventPushOfTypeTicketWhatsAppUnsupportedMessage(tpn));
    } else if (tpn.type === 'ticket_new_ticket') {
        actions.push(onTopEventPushOfTypeTicketCreatedReceived(tpn));
    } else if (tpn.type === 'ticket_awaiting_activity_set') {
        actions.push(onTopEventTicketAwaitingActivitySet(tpn));
    } else if (tpn.type === 'ticket_subject_set') {
        actions.push(onTopEventTicketSubjectSet(tpn));
    } else if (tpn.type === 'ticket_visibility_set') {
        actions.push(onTopEventTicketVisibilitySet(tpn));
    } else if (tpn.type === 'ticket_transcription_progress') {
        actions.push(onTopEventTicketTranscriptionProgress(tpn));
    } else if (tpn.type === 'ticket_members_change') {
        actions.push(onTopEventPushOfTypeTicketMembersChanged(tpn));
    } else if (tpn.type === 'chat_session_archive') {
        const { data } = tpn;
        const { channelDTO, sessionDTO } = JSON.parse(atou(data.dto));
        actions.push(receiveArchived(channelDTO.key, sessionDTO));
    } else if (tpn.type === 'chat_session_unarchive') {
        const { data } = tpn;
        const { channelDTO, sessionDTO } = JSON.parse(atou(data.dto));
        actions.push(receiveUnarchived(channelDTO.key, sessionDTO));
    } else if (tpn.type === 'video_incoming_call') {
        actions.push(
            handleVideo2IncomingCall({
                extensionKey: tpn.data.extensionKey,
                meetingCode: tpn.data.meetingCode,
            }),
        );
    } else if (tpn.type === 'video_call_declined') {
        actions.push(
            handleVideo2CallDeclined({
                extensionKey: tpn.data.extensionKey,
                meetingCode: tpn.data.meetingCode,
            }),
        );
    } else if (tpn.type === 'video_call_answered') {
        actions.push(
            handleVideo2CallAccepted({
                extensionKey: tpn.data.extensionKey,
            }),
        );
    } else if (tpn.type === 'video_call_canceled') {
        actions.push(
            handleVideo2CallCanceled({
                extensionKey: tpn.data.extensionKey,
                meetingCode: tpn.data.meetingCode,
            }),
        );
    } else if (tpn.type === 'video_call_handled_on_other_device') {
        actions.push(
            handleVideo2CallHandledOnOtherDevice({
                extensionKey: tpn.data.extensionKey,
                meetingCode: tpn.data.meetingCode,
            }),
        );
    } else if (tpn.type === 'chat_reaction') {
        if (tpn.data.from !== getOwnExtensionKey(getState())) {
            const json = JSON.parse(atou(tpn.data.dto));
            actions.push(
                updateReactions(
                    json.chatSessionKey,
                    json.messageKey,
                    json.reactionDTO,
                    json?.threadParentKey,
                ),
            );
        }
    } else if (tpn.type === 'call_recording_transcription_progress') {
        actions.push(onTopEventCallRecordingTranscriptionProgress(tpn));
    } else if (tpn.type === 'companion_call_control') {
        actions.push(handleCompanionCallControl(tpn.data));
    } else if (tpn.type === 'companion_designation') {
        actions.push(handleCompanionDesignation(tpn.data));
    } else if (tpn.type === 'companion_location_status') {
        actions.push(handleCompanionLocationStatus(tpn.data));
    }

    return actions;
};

const handlePing = () => {
    const pong = {
        Response: 'Pong',
    };

    _sendRequest(pong);
};

const handleWelcome = (msg, username, password, anumber) => {
    const auth = {
        Action: 'Login',
        Username: username,
        Password: md5(password + msg.Nonce),
        Anumber: anumber,
        Version: '1.0',
        PushProfiles: 'true',
        PushNotifications: 'true',
        PushQueueAgents: 'true',
        ExternalChannels: 'true',
        SkipExtensionProfiles: 'true',
        PushConferenceMemberCount: 'true',
        OperatorClientType: getClientType(isElectron()),
        IncludeExtensionsWithoutSIPUser: 'true',
        UseNumberInfo: 'true',
    };

    _sendRequest(auth);
};

class TopDebugTool {
    counter = {
        total: 0,
    };

    byteCounter = {
        total: 0,
    };

    startTime = Date.now();

    enabled = false;

    updateCounter = (msg, size) => {
        const key = (() => {
            if (msg.Action) {
                return 'action-' + msg.Action;
            } else if (msg.Event) {
                return 'event-' + msg.Event;
            } else if (msg.Response) {
                return 'response-' + msg.Response;
            } else {
                console.warn('Unknown message', msg);

                return 'unknown';
            }
        })();

        if (!this.counter[key]) {
            this.counter[key] = 0;
        }

        this.counter[key]++;
        this.counter['total']++;

        if (!this.byteCounter[key]) {
            this.byteCounter[key] = 0;
        }

        this.byteCounter['total'] += size;
        this.byteCounter[key] += size;

        if (this.enabled && this.counter['total'] % 10 === 0) {
            console.group(
                'Top message count: ' +
                    this.counter['total'] +
                    ' messages' +
                    ' in ' +
                    this.byteCounter['total'] +
                    ' bytes' +
                    ' during ' +
                    (Date.now() - this.startTime) +
                    'ms',
            );

            const percent = (n, tot) => Math.round((n / tot) * 1000) / 10;

            Object.entries(this.counter)
                .filter((e) => e[0] !== 'total')
                .sort((x, y) => y[1] - x[1])
                .forEach((d) => {
                    const msg = `${d[1]} (${percent(
                        d[1],
                        this.counter['total'],
                    )}%)`;
                    const byt = `${this.byteCounter[d[0]]} bytes (${percent(
                        this.byteCounter[d[0]],
                        this.byteCounter['total'],
                    )}%)`;

                    console.info(d[0] + ' => ' + msg + ' ' + byt);
                });

            console.groupEnd();
        }
    };
}

if (process.env.NODE_ENV === 'devel') {
    console.info('Activate Top debug using: topDebug.enabled=true');

    window.topDebug = new TopDebugTool();
}

const topMessageHandler =
    (anumber, username, password) => (dispatch, getState) => (msg) => {
        const rx = JSON.parse(msg.data);

        if (topMessageHandler.syncing === false) {
            // addTopLogEntry(msg.data); //Not sending all top events to log at the moment since it is too spammy
        }

        if (window.topDebug) {
            window.topDebug.updateCounter(rx, msg.data.length);
        }

        if (topMessageHandler.syncing) {
            if (rx.Event === 'SyncStop') {
                syncMessages.forEach((message) =>
                    messageDispatcher(
                        anumber,
                        username,
                        password,
                    )(
                        dispatch,
                        getState,
                    )(message),
                );
                syncMessages = [];
            } else {
                syncMessages.push(rx);
                return;
            }
        }

        messageDispatcher(anumber, username, password)(dispatch, getState)(rx);
    };

const messageDispatcher =
    (anumber, username, password) => (dispatch, getState) => (rx) => {
        if (rx.Action === 'Ping') {
            addTopLogEntry('Received: Ping');
            handlePing();
        } else if (rx.Response === 'Pong') {
            addTopLogEntry('Received: Pong');
        } else if (rx.Response === 'Error') {
            handleError(rx);
        } else if (rx.Event === 'Welcome') {
            handleWelcome(rx, username, password, anumber);
        } else if (rx.Event === 'Push') {
            const payload = atou(rx.Payload);
            const json = JSON.parse(payload);
            const actions = pushToActions(getState, json);

            if (actions) {
                actions.map(dispatch);
            } else {
                if (appConfig.environment === 'dev') {
                    console.warn(
                        'Failed to convert TPN to DTO',
                        payload,
                        actions,
                    );
                }
            }
        } else if (rx.Event === 'Extension') {
            dispatch(receiveExtension(rx, true));
        } else if (rx.Event === 'SyncStart') {
            // eslint-disable-line no-empty
            topMessageHandler.syncing = true;
            addTopLogEntry('Extension sync started');
            queueMemberEventsByQueue.clear();
            dispatch(clearMeetingMemberCountsAction());
            dispatch(receiveSyncStart);
        } else if (rx.Event === 'SyncStop') {
            // eslint-disable-line no-empty
            topMessageHandler.syncing = false;
            addTopLogEntry('Extension sync stopped');
            dispatch(receiveQueueMembers(queueMemberEventsByQueue));
            dispatch(receiveSyncStop);
            // A hangup sweeps on its own; this covers the fresh start, where the active call list
            // is only complete once the sync has finished and a record left behind by a client
            // that was killed mid-call would otherwise survive.
            dispatch(sweepCompanionMuteState());
        } else if (rx.Event === 'Queue') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'QueueAgent') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'QueueJoin') {
            // eslint-disable-line no-empty
            dispatch(receiveQueueJoinEvent(rx));
        } else if (rx.Event === 'QueueLeave') {
            // eslint-disable-line no-empty
            dispatch(receiveQueueLeaveEvent(rx));
        } else if (rx.Event === 'QueueMember') {
            if (!queueMemberEventsByQueue.has(rx.Queue)) {
                queueMemberEventsByQueue.set(rx.Queue, [rx]);
            } else {
                queueMemberEventsByQueue.get(rx.Queue).push(rx);
            }
        } else if (rx.Event === 'Refer') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'QueueCallInfo') {
            // eslint-disable-line no-empty
            dispatch(receiveQueueCallInfo(rx));
        } else if (rx.Event === 'Profile') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'Redirects') {
            dispatch(receiveActiveProfile(rx));
        } else if (rx.Event === 'Link') {
            dispatch(receiveLink(rx));
        } else if (rx.Event === 'Unlink') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'Rename') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'NewState') {
            // eslint-disable-line no-empty
            dispatch(receiveNewState(rx));
        } else if (rx.Event === 'QueueAgentLoggedIn') {
            // eslint-disable-line no-empty
            dispatch(receiveQueueAgentActivity(rx, true));
        } else if (rx.Event === 'QueueAgentLoggedOut') {
            // eslint-disable-line no-empty
            dispatch(receiveQueueAgentActivity(rx, false));
        } else if (rx.Event === 'QueueAgentAdded') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'OutDial') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'MexIn') {
            // eslint-disable-line no-empty
            dispatch(receiveMexIn(rx));
        } else if (rx.Event === 'MexOut') {
            // eslint-disable-line no-empty
            dispatch(receiveMexOut(rx));
        } else if (rx.Event === 'NewCallerID') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'UpdateCallerID') {
            dispatch(receiveUpdateCallerID(rx));
        } else if (rx.Event === 'Hangup') {
            dispatch(receiveHangup(rx));
        } else if (rx.Event === 'NewChannel') {
            // eslint-disable-line no-empty
            dispatch(receiveNewChannel(rx));
        } else if (rx.Event === 'CallSuccess') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'CallFailure') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'VoicemailStatus') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'Dial') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'AttendedTransferConfirmed') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'AttendedTransferCancelled') {
            dispatch(receiveAttendedTransferCancelled(rx));
        } else if (rx.Event === 'AttendedTransferCompleted') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'ParkedCallGiveUp') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'ParkedCall') {
            // eslint-disable-line no-empty
        } else if (rx.Event === 'NumberInfo') {
            dispatch(receiveNumberInfo(rx));
        } else if (rx.Event === 'GoAway') {
            dispatch(receiveGoAway);
        } else if (rx.Event === 'SetBagVariable') {
            if (rx.Uniqueid) {
                // Uniqueid is named UniqueID in other events
                // Remove when/if fixed in backend
                rx.UniqueID = rx.Uniqueid;
            }
            dispatch(receiveBagVariable(rx));
        } else if (rx.Event === 'CoMonitoredStart') {
            dispatch(receiveCoMonitoredStart(rx));
        } else if (rx.Event === 'CoMonitoredEnd') {
            dispatch(receiveCoMonitoredEnd(rx));
        } else if (rx.Event === 'ExternalChannel') {
            dispatch(receiveExternalChannel(rx));
        } else if (rx.Event === 'MergedIntoConference') {
            dispatch(receiveMergedIntoConference(rx));
        } else if (rx.Response === 'OK') {
            // eslint-disable-line no-empty
            handleOk(rx);
        } else if ('ConferenceMemberCount' === rx.Event) {
            dispatch(recieveMeetingMemberCount(rx));
        } else {
            console.warn('Unhandled message: ', rx);
        }
    };

/**
 * Only to be used for tests. Mocks a socket and bounces the request back as a response.
 */
export const mockCreateSocket = () => {
    socket = {
        send: (request) => {
            const actionId = JSON.parse(request).ActionID;
            if (!isNaN(actionId)) {
                const response = JSON.parse(request);
                response.ActionID = actionId.toString();
                if (response.type === 'FAIL') {
                    handleError(response);
                } else {
                    handleOk(response);
                }
            }
        },
        readyState: WebSocket.OPEN,
    };
};

/**
 * Action Redux thunk for creating the Top web socket.
 *
 * Everything belonging to one connection - its ping timers and whether it has finished - lives in
 * this closure rather than in the module, and a socket only touches the module state while it is
 * the current one. A socket given up on keeps firing events until the browser finishes closing it,
 * which on a dead connection can be long after its replacement is up; if it could still clear the
 * shared socket and ping timers, it would leave the replacement unable to send and with nothing
 * watching it, receiving until its connection silently died and then never reconnecting.
 */
const createSocket =
    (endpoint, anumber, username, password, onCloseHandler, retrieveLiveData) =>
    (dispatch, getState) => {
        topMessageHandler.syncing = false;
        // Events a given-up socket buffered during its sync must not be replayed by the next one
        syncMessages = [];
        pendingRequests.clear();

        if (currentConnection) {
            currentConnection.abandon('Replaced by a new connection');
        }

        return new Promise((resolve, reject) => {
            const ws = new WebSocket(endpoint);
            let pingInterval = undefined;
            let pingTimeout = 0;
            let connectTimeout = 0;
            let finished = false;

            const clearPingTimeout = () => {
                clearTimeout(pingTimeout);
                pingTimeout = 0;
            };

            const isCurrent = () => currentConnection?.ws === ws;

            // Runs once per socket, from whichever comes first: the close event, or us deciding
            // the connection is dead. The latter does not wait for the close event, since closing
            // a connection that is already gone can take the browser a long time.
            const finish = (notifyClose) => {
                if (finished) {
                    return;
                }
                finished = true;

                clearTimeout(connectTimeout);
                clearInterval(pingInterval);
                clearPingTimeout();

                if (!isCurrent()) {
                    return;
                }

                currentConnection = null;
                socket = null;

                if (notifyClose && onCloseHandler) {
                    onCloseHandler();
                }
            };

            const giveUp = (reason, notifyClose) => {
                addTopLogEntry(
                    `Giving up Top socket to url ${ws.url}: ${reason}`,
                );
                // Closed first, so that the close handler already sees the socket as not open.
                try {
                    ws.close(CLOSE_EVENT_NORMAL_CODE, reason);
                } finally {
                    finish(notifyClose);
                }
            };

            const armPingTimeout = (timeoutMs) => {
                clearPingTimeout();
                pingTimeout = setTimeout(
                    () => giveUp(`Pong not received in ${timeoutMs} ms`, true),
                    timeoutMs,
                );
            };

            const sendPing = () => {
                if (ws.readyState !== WebSocket.OPEN) {
                    return;
                }
                ws.send(JSON.stringify({ Action: 'Ping' }));
                addTopLogEntry('Sent: Ping');
            };

            currentConnection = {
                ws,
                probe: () => {
                    if (finished || ws.readyState !== WebSocket.OPEN) {
                        return;
                    }
                    sendPing();
                    armPingTimeout(PROBE_TIMEOUT_MS);
                },
                // A replaced socket must not reconnect: its replacement is already on the way.
                abandon: (reason) => giveUp(reason, false),
            };
            socket = ws;

            connectTimeout = setTimeout(() => {
                giveUp(`Not open within ${CONNECT_TIMEOUT_MS} ms`, true);
                reject(new Error('Top socket did not open in time'));
            }, CONNECT_TIMEOUT_MS);

            ws.onopen = () => {
                clearTimeout(connectTimeout);

                if (!isCurrent()) {
                    ws.close(
                        CLOSE_EVENT_NORMAL_CODE,
                        'Replaced by a new connection',
                    );
                    reject(new Error('Top socket replaced before it opened'));
                    return;
                }

                addTopLogEntry(`Top socket established to url ${ws.url}`);
                console.info('Socket established');

                pingInterval = setInterval(() => {
                    sendPing();
                    if (!pingTimeout) {
                        armPingTimeout(PING_TIMEOUT_MS);
                    }
                }, PING_INTERVAL_MS);

                // Resolved before the side effects below, so that one of them failing cannot
                // leave the connection unregistered and every later reconnect blocked behind it.
                resolve(ws);

                try {
                    if (retrieveLiveData) {
                        dispatch(comeBackAPICallbacks);
                    }

                    // A surface that came up while this companion was quiet has no status at all, and
                    // absent reads as "nothing to complain about" - so it would let the user dial and
                    // the call would fail with nothing shown. Reporting on every connect is what
                    // bounds how long a surface can be wrong about a companion that cannot get a
                    // position.
                    dispatch(reportKnownCompanionLocationStatus());
                } catch (error) {
                    addTopLogEntry(
                        'Failed to run Top connect handlers:',
                        error?.message,
                    );
                    console.error('Failed to run Top connect handlers', error);
                }
            };

            ws.onerror = (event) => {
                addTopLogEntry('Top socket error message:', event?.message);
                reject(event);
            };

            ws.onclose = (closeEvent) => {
                addTopLogEntry(
                    `Top socket disconnected from url ${
                        ws.url
                    }: '${JSON.stringify(closeEvent)} because of reason: ${
                        closeEvent?.reason
                    }'`,
                );
                console.info('Socket disconnected');

                finish(true);
            };

            const handleMessage = topMessageHandler(
                anumber,
                username,
                password,
            )(dispatch, getState);

            ws.onmessage = (msg) => {
                // Late messages from a socket given up on would be handled twice, or out of
                // order with those of its replacement.
                if (finished || !isCurrent()) {
                    return;
                }
                clearPingTimeout();
                handleMessage(msg);
            };
        });
    };

export default createSocket;
